import { CurrencyCode } from '@payflip/utils';
import { currencySymbolMap } from '../currency-input/currency-input';
import { Row } from '../row';
import {
  CurrencySymbol,
  InputWrapper,
  ReadOnlyInput,
  StyledButton
} from './styled';

export enum ButtonSize {
  large = 'large',
  medium = 'medium',
  small = 'small'
}

export interface CounterInputProps {
  value?: number;
  onChange: (v: number) => void;
  max?: number;
  min?: number;
  step?: number | number[];
  size?: ButtonSize;
  disabled?: boolean;
  currency?: CurrencyCode;
  required?: boolean;
}

export function CounterInput({
  currency,
  size = ButtonSize.medium,
  disabled = false,
  required = false,
  value = 0,
  max,
  min = 0,
  step = 1,
  onChange
}: CounterInputProps): JSX.Element {
  const decreaseButtonDisabled = disabled || value <= min;

  const increaseButtonDisabled =
    disabled || (typeof max === 'number' && value >= max);

  const increase = (): void => {
    if (typeof max === 'number' && value >= max) {
      return;
    }

    if (typeof step === 'number') {
      if (typeof max === 'number' && value + step >= max) {
        onChange(max);
      }

      onChange(value + step);
    }

    if (Array.isArray(step)) {
      const upperIdx = step.length - 1;
      const idx = step.indexOf(value);
      if (idx === -1) {
        return;
      }

      const nextIdx = idx + 1;
      if (nextIdx > upperIdx) {
        return;
      }

      onChange(step[nextIdx]);
    }
  };

  const decrease = (): void => {
    if (value <= min) {
      return;
    }

    if (typeof step === 'number') {
      if (value - step <= min) {
        onChange(min);
      }

      onChange(value - step);
    }

    if (Array.isArray(step)) {
      const lowerIdx = 0;
      const idx = step.indexOf(value);
      if (idx === -1) {
        return;
      }

      const nextIdx = idx - 1;
      if (nextIdx < lowerIdx) {
        return;
      }

      onChange(step[nextIdx]);
    }
  };

  return (
    <Row alignItems="center">
      <StyledButton
        size={size}
        disabled={decreaseButtonDisabled}
        onClick={decrease}
        type="button"
      >
        -
      </StyledButton>
      <InputWrapper>
        {currency && (
          <CurrencySymbol variant={size}>
            {currencySymbolMap[currency]}
          </CurrencySymbol>
        )}
        <ReadOnlyInput
          variant={size}
          value={value}
          readOnly
          required={required}
        />
      </InputWrapper>
      <StyledButton
        size={size}
        disabled={increaseButtonDisabled}
        onClick={increase}
        type="button"
      >
        +
      </StyledButton>
    </Row>
  );
}
